import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Users, ShieldCheck, ShieldBan, Activity, FolderOpen, CreditCard, Search, CheckCircle, XCircle, RotateCcw, Eye, TrendingUp, Crown, ArrowUpRight, MessageSquare, Mail, Send, Globe, Ticket, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import CouponsTab from '../components/CouponsTab';

const CHART_COLORS = ['#1A4D2E', '#B45309', '#8B2626', '#0E7490', '#6D28D9', '#9333EA', '#D97706', '#059669', '#DC2626', '#2563EB'];
const PIE_COLORS = ['#1A4D2E', '#B45309', '#0E7490', '#8B2626', '#6D28D9', '#D97706'];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [grantTarget, setGrantTarget] = useState(null);  // doctor obj for manual-subscribe modal
  const [grantPlan, setGrantPlan] = useState('6_months');
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);

  const fetchData = async () => {
    try {
      const [s, d, a, m, v] = await Promise.allSettled([
        api.get('/admin/stats'),
        api.get('/admin/doctors'),
        api.get('/admin/analytics'),
        api.get('/admin/contact-messages'),
        api.get('/admin/visitor-stats'),
      ]);
      if (s.status === 'fulfilled') setStats(s.value.data);
      if (d.status === 'fulfilled') setDoctors(d.value.data);
      if (a.status === 'fulfilled') setAnalytics(a.value.data);
      if (m.status === 'fulfilled') setMessages(m.value.data);
      if (v.status === 'fulfilled') setVisitorStats(v.value.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const markRead = async (msgId) => {
    try {
      await api.post(`/admin/contact-messages/${msgId}/read`);
    } catch (e) { /* non-critical */ }
    setMessages((prev) => prev.map(m => m.id === msgId ? { ...m, status: 'read' } : m));
  };

  const sendReply = async (msgId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const { data } = await api.post(`/admin/contact-messages/${msgId}/reply`, { reply_text: replyText });
      setMessages(messages.map(m => m.id === msgId ? { ...m, status: 'replied', replies: [...(m.replies || []), data.reply] } : m));
      toast.success(data.message);
      setReplyingTo(null);
      setReplyText('');
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleAction = async (doctorId, action) => {
    setActionLoading(`${doctorId}-${action}`);
    try {
      await api.post(`/admin/doctors/${doctorId}/${action}`);
      toast.success(`Doctor ${action === 'promote' ? 'promoted to admin' : action === 'demote' ? 'demoted to doctor' : action + 'ed'} successfully`);
      fetchData();
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const submitGrant = async () => {
    if (!grantTarget) return;
    setGranting(true);
    try {
      await api.post(`/admin/doctors/${grantTarget._id}/manual-subscribe`, { plan: grantPlan, note: grantNote });
      toast.success(`${grantTarget.name || grantTarget.email} now has ${grantPlan.replace('_', ' ')} plan`);
      setGrantTarget(null);
      setGrantPlan('6_months');
      setGrantNote('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant subscription');
    } finally {
      setGranting(false);
    }
  };

  const nmcVerifyUrl = (doc) => {
    const regNum = doc.mci_registration_number || '';
    const name = doc.name || '';
    return `https://www.nmc.org.in/information-desk/indian-medical-register/?registration_number=${encodeURIComponent(regNum)}&name=${encodeURIComponent(name)}`;
  };

  const filtered = doctors.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.email?.toLowerCase().includes(search.toLowerCase()) ||
    d.mci_registration_number?.toLowerCase().includes(search.toLowerCase())
  );

  const statCards = [
    { label: 'Total Doctors', value: stats?.total_doctors, icon: Users, color: 'text-stone-600', bg: 'bg-stone-50' },
    { label: 'Verified', value: stats?.verified_doctors, icon: ShieldCheck, color: 'text-[#1A4D2E]', bg: 'bg-[#1A4D2E]/5' },
    { label: 'Pending', value: stats?.pending_doctors, icon: Activity, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Blocked', value: stats?.blocked_doctors, icon: ShieldBan, color: 'text-[#8B2626]', bg: 'bg-red-50' },
    { label: 'Total Cases', value: stats?.total_cases, icon: FolderOpen, color: 'text-stone-600', bg: 'bg-stone-50' },
    { label: 'Payments', value: stats?.total_payments, icon: CreditCard, color: 'text-[#1A4D2E]', bg: 'bg-[#1A4D2E]/5' },
  ];

  const roleBadge = (role) => {
    if (role === 'super_admin') return <Badge className="bg-purple-600 text-white">Super Admin</Badge>;
    if (role === 'admin') return <Badge className="bg-blue-600 text-white">Admin</Badge>;
    return null;
  };

  const statusBadge = (d) => {
    if (d.role === 'super_admin') return <Badge className="bg-purple-600 text-white">Super Admin</Badge>;
    if (d.role === 'admin') return <Badge className="bg-blue-600 text-white">Admin</Badge>;
    if (d.is_blocked) return <Badge className="bg-[#8B2626] text-white">Blocked</Badge>;
    if (d.is_verified) return <Badge className="bg-[#1A4D2E] text-white">Verified</Badge>;
    return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">Admin Panel</h1>
            {isSuperAdmin && <Crown className="w-6 h-6 text-amber-500" />}
          </div>
          <p className="text-stone-500 mt-1">
            {isSuperAdmin ? 'Super Admin — Full platform control, user management, and analytics' : 'Manage doctors, verify registrations, and monitor activity'}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((s, i) => (
          <Card key={i} className="border-stone-200 shadow-sm">
            <CardContent className="pt-4 pb-3 px-4">
              <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-2`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className="text-2xl font-mono text-stone-900">{loading ? '...' : s.value ?? 0}</p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="analytics" className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="analytics" className="data-[state=active]:bg-white gap-1.5">
            <TrendingUp className="w-4 h-4" /> Analytics
          </TabsTrigger>
          <TabsTrigger value="doctors" className="data-[state=active]:bg-white gap-1.5">
            <Users className="w-4 h-4" /> Doctors
          </TabsTrigger>
          <TabsTrigger value="messages" className="data-[state=active]:bg-white gap-1.5">
            <MessageSquare className="w-4 h-4" /> Messages
            {messages.filter(m => m.status === 'unread').length > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full bg-[#8B2626] text-white text-[10px] font-bold flex items-center justify-center">
                {messages.filter(m => m.status === 'unread').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="visitors" className="data-[state=active]:bg-white gap-1.5">
            <Globe className="w-4 h-4" /> Visitors
          </TabsTrigger>
          <TabsTrigger value="coupons" className="data-[state=active]:bg-white gap-1.5" data-testid="coupons-tab-trigger">
            <Ticket className="w-4 h-4" /> Coupons
          </TabsTrigger>
        </TabsList>

        {/* ─── Analytics Tab ─── */}
        <TabsContent value="analytics" className="mt-4 space-y-4">
          {!analytics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-64 bg-stone-100 rounded-xl animate-pulse" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Cases Over Time */}
                <Card className="border-stone-200 shadow-sm" data-testid="chart-cases-over-time">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#1A4D2E]" /> Cases Over Time
                      <span className="text-xs text-stone-400 font-normal">(Last 30 days)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={analytics.cases_per_day}>
                        <defs>
                          <linearGradient id="casesGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1A4D2E" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#1A4D2E" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Area type="monotone" dataKey="count" stroke="#1A4D2E" fill="url(#casesGrad)" strokeWidth={2} name="Cases" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Top Diagnoses */}
                <Card className="border-stone-200 shadow-sm" data-testid="chart-top-diagnoses">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4 text-[#B45309]" /> Top Diagnoses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.top_diagnoses} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(val, name) => name === 'avg_probability' ? `${val}%` : val} />
                        <Bar dataKey="count" fill="#1A4D2E" radius={[0, 4, 4, 0]} name="Occurrences" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Plan Distribution */}
                <Card className="border-stone-200 shadow-sm" data-testid="chart-plan-distribution">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-[#0E7490]" /> Plan Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={analytics.plan_distribution} dataKey="count" nameKey="plan" cx="50%" cy="50%" outerRadius={80} label={({ plan, count }) => `${plan?.replace(/_/g, ' ')} (${count})`} labelLine={false}>
                          {analytics.plan_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Case Status */}
                <Card className="border-stone-200 shadow-sm" data-testid="chart-case-status">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-stone-600" /> Case Status Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={analytics.case_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ status, count }) => `${status} (${count})`} labelLine={false}>
                          {analytics.case_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Severity Distribution */}
                <Card className="border-stone-200 shadow-sm" data-testid="chart-severity">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <ShieldBan className="w-4 h-4 text-[#8B2626]" /> Severity Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={analytics.severity_distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                        <XAxis dataKey="severity" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Cases">
                          {analytics.severity_distribution.map((entry, i) => (
                            <Cell key={i} fill={entry.severity === 'severe' ? '#8B2626' : entry.severity === 'moderate' ? '#B45309' : entry.severity === 'mild' ? '#D97706' : '#1A4D2E'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Doctor Registrations */}
                <Card className="border-stone-200 shadow-sm" data-testid="chart-registrations">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#6D28D9]" /> Doctor Registrations
                      <span className="text-xs text-stone-400 font-normal">(Last 30 days)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={analytics.registrations_per_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Line type="monotone" dataKey="count" stroke="#6D28D9" strokeWidth={2} dot={{ r: 3 }} name="Registrations" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Top Diagnoses Table */}
              {analytics.top_diagnoses?.length > 0 && (
                <Card className="border-stone-200 shadow-sm" data-testid="top-diagnoses-table">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-heading">Diagnosis Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase tracking-wider">Rank</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider">Disease</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider">Occurrences</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider">Avg. Probability</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.top_diagnoses.map((d, i) => (
                          <TableRow key={d.name}>
                            <TableCell className="font-mono text-sm text-stone-500">#{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium text-stone-900">{d.name}</TableCell>
                            <TableCell className="text-sm font-mono text-stone-700">{d.count}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${d.avg_probability}%`, backgroundColor: d.avg_probability >= 60 ? '#8B2626' : d.avg_probability >= 30 ? '#B45309' : '#1A4D2E' }} />
                                </div>
                                <span className="text-xs font-mono text-stone-600">{d.avg_probability}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ─── Doctors Tab ─── */}
        <TabsContent value="doctors" className="mt-4">
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-lg font-heading">All Users ({filtered.length})</CardTitle>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input data-testid="admin-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or MCI..." className="pl-9 h-9" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-stone-100 rounded animate-pulse" />)}</div>
              ) : (
                <Table data-testid="admin-doctors-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider">User</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">MCI/NMC</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Clinic</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Cases</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Plan</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(d => (
                      <TableRow key={d._id} data-testid={`admin-doctor-row-${d._id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="text-sm font-medium text-stone-900">{d.name}</p>
                              <p className="text-xs text-stone-400">{d.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {d.mci_registration_number ? (
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-sm text-stone-700">{d.mci_registration_number}</span>
                              <a href={nmcVerifyUrl(d)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Verify on NMC" data-testid={`nmc-verify-link-${d._id}`}>
                                <ArrowUpRight className="w-3.5 h-3.5 text-[#1A4D2E] hover:text-[#143B23]" />
                              </a>
                            </div>
                          ) : <span className="text-sm text-stone-400">—</span>}
                        </TableCell>
                        <TableCell className="text-sm text-stone-600">{d.clinic_name || '—'}</TableCell>
                        <TableCell className="text-sm font-mono text-stone-700">{d.case_count}</TableCell>
                        <TableCell className="text-xs text-stone-600 capitalize">{d.subscription?.plan?.replace(/_/g, ' ') || '—'}</TableCell>
                        <TableCell>{statusBadge(d)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelected(d)} data-testid={`admin-view-${d._id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {d.role === 'doctor' && (
                              <>
                                {!d.is_verified && !d.is_blocked && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#1A4D2E]" onClick={() => handleAction(d._id, 'verify')} data-testid={`admin-verify-${d._id}`}>
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                {!d.is_blocked ? (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8B2626]" onClick={() => handleAction(d._id, 'block')} data-testid={`admin-block-${d._id}`}>
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" onClick={() => handleAction(d._id, 'unblock')} data-testid={`admin-unblock-${d._id}`}>
                                    <RotateCcw className="w-4 h-4" />
                                  </Button>
                                )}
                                {isSuperAdmin && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-600" onClick={() => handleAction(d._id, 'promote')} data-testid={`admin-promote-${d._id}`} title="Promote to Admin">
                                    <Crown className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => { setGrantTarget(d); setGrantPlan('6_months'); setGrantNote(''); }} data-testid={`admin-grant-${d._id}`} title="Grant complimentary subscription">
                                  <Gift className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {d.role === 'admin' && isSuperAdmin && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-500" onClick={() => handleAction(d._id, 'demote')} data-testid={`admin-demote-${d._id}`} title="Demote to Doctor">
                                <ArrowUpRight className="w-4 h-4 rotate-180" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Messages Tab ─── */}
        <TabsContent value="messages" className="mt-4">
          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading">Contact Messages ({messages.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {!messages.length ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No contact messages yet</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="messages-list">
                  {messages.map(msg => (
                    <div key={msg.id} data-testid={`message-${msg.id}`}
                      className={`p-4 rounded-xl border transition-colors ${msg.status === 'unread' ? 'border-[#1A4D2E]/30 bg-[#1A4D2E]/[0.02]' : msg.status === 'replied' ? 'border-stone-100 bg-white' : 'border-stone-100 bg-white'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-stone-900">{msg.name}</p>
                            {msg.status === 'unread' && <span className="w-2 h-2 rounded-full bg-[#1A4D2E] shrink-0" />}
                            {msg.status === 'replied' && <Badge className="bg-[#1A4D2E] text-white text-[10px] py-0">Replied</Badge>}
                            <span className="text-xs text-stone-400">{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Mail className="w-3 h-3 text-stone-400" />
                            <span className="text-xs text-stone-500">{msg.email}</span>
                          </div>
                          <p className="text-sm font-medium text-stone-800 mt-2">{msg.subject}</p>
                          <p className="text-sm text-stone-600 mt-1 leading-relaxed">{msg.message}</p>

                          {/* Previous replies */}
                          {msg.replies?.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {msg.replies.map((r, i) => (
                                <div key={i} className="pl-3 border-l-2 border-[#1A4D2E]/30">
                                  <p className="text-xs text-stone-400">{r.by} replied {new Date(r.at).toLocaleString()}</p>
                                  <p className="text-sm text-stone-700 mt-0.5">{r.text}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Reply input */}
                          {replyingTo === msg.id && (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                data-testid={`reply-input-${msg.id}`}
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                placeholder={`Reply to ${msg.name}...`}
                                rows={3}
                                className="text-sm"
                              />
                              <div className="flex gap-2">
                                <Button size="sm" disabled={sendingReply || !replyText.trim()} onClick={() => sendReply(msg.id)}
                                  className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-1.5 text-xs" data-testid={`send-reply-${msg.id}`}>
                                  <Send className="w-3 h-3" /> {sendingReply ? 'Sending...' : 'Send Reply'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(''); }} className="text-xs text-stone-500">
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          {msg.status === 'unread' && (
                            <Button variant="ghost" size="sm" onClick={() => markRead(msg.id)} className="text-xs text-stone-500" data-testid={`mark-read-${msg.id}`}>
                              Mark read
                            </Button>
                          )}
                          {replyingTo !== msg.id && (
                            <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(msg.id); setReplyText(''); }} className="text-xs text-[#1A4D2E]" data-testid={`reply-btn-${msg.id}`}>
                              <Send className="w-3 h-3 mr-1" /> Reply
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Visitors Tab ─── */}
        <TabsContent value="visitors" className="mt-4 space-y-4">
          {/* Summary cards */}
          {visitorStats?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-stone-200 shadow-sm">
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="w-8 h-8 rounded-lg bg-[#1A4D2E]/5 flex items-center justify-center mb-2">
                    <Eye className="w-4 h-4 text-[#1A4D2E]" />
                  </div>
                  <p className="text-2xl font-mono text-stone-900">{visitorStats.summary.today_views}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 mt-0.5">Today Page Views</p>
                </CardContent>
              </Card>
              <Card className="border-stone-200 shadow-sm">
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mb-2">
                    <Users className="w-4 h-4 text-amber-600" />
                  </div>
                  <p className="text-2xl font-mono text-stone-900">{visitorStats.summary.today_unique}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 mt-0.5">Today Unique Visitors</p>
                </CardContent>
              </Card>
              <Card className="border-stone-200 shadow-sm">
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mb-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-2xl font-mono text-stone-900">{visitorStats.summary.total_views_30d}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 mt-0.5">30-Day Page Views</p>
                </CardContent>
              </Card>
              <Card className="border-stone-200 shadow-sm">
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center mb-2">
                    <Users className="w-4 h-4 text-purple-600" />
                  </div>
                  <p className="text-2xl font-mono text-stone-900">{visitorStats.summary.total_unique_30d}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 mt-0.5">30-Day Unique Visitors</p>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Daily visits chart */}
            <Card className="border-stone-200 shadow-sm" data-testid="chart-daily-visits">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#1A4D2E]" /> Daily Visits
                  <span className="text-xs text-stone-400 font-normal">(Last 30 days)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visitorStats?.daily?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={visitorStats.daily}>
                      <defs>
                        <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1A4D2E" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#1A4D2E" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="uniqueGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#B45309" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#B45309" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="total_views" stroke="#1A4D2E" fill="url(#viewsGrad)" strokeWidth={2} name="Page Views" />
                      <Area type="monotone" dataKey="unique_visitors" stroke="#B45309" fill="url(#uniqueGrad)" strokeWidth={2} name="Unique Visitors" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-stone-400 text-sm">No visit data yet</div>
                )}
              </CardContent>
            </Card>

            {/* Top pages */}
            <Card className="border-stone-200 shadow-sm" data-testid="chart-top-pages">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-[#0E7490]" /> Top Pages
                </CardTitle>
              </CardHeader>
              <CardContent>
                {visitorStats?.top_pages?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={visitorStats.top_pages} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis dataKey="page" type="category" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="views" fill="#1A4D2E" radius={[0, 4, 4, 0]} name="Views" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-stone-400 text-sm">No page data yet</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── Coupons Tab ─── */}
        <TabsContent value="coupons" className="mt-4">
          <CouponsTab />
        </TabsContent>
      </Tabs>

      {/* Doctor detail dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">{selected?.name} {roleBadge(selected?.role)}</DialogTitle>
            <DialogDescription>{selected?.email}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-stone-50">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">MCI/NMC Number</p>
                  <p className="font-mono font-medium mt-0.5">{selected.mci_registration_number || 'Not provided'}</p>
                  {selected.mci_registration_number && (
                    <a href={nmcVerifyUrl(selected)} target="_blank" rel="noopener noreferrer"
                      data-testid="nmc-verify-btn"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1A4D2E] bg-[#1A4D2E]/10 px-3 py-1.5 rounded-md hover:bg-[#1A4D2E]/20 transition-colors">
                      <ArrowUpRight className="w-3 h-3" /> Verify on NMC
                    </a>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-stone-50">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Phone</p>
                  <p className="font-medium mt-0.5">{selected.phone || 'Not provided'}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Clinic</p>
                  <p className="font-medium mt-0.5">{selected.clinic_name || 'Not provided'}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Specialization</p>
                  <p className="font-medium mt-0.5">{selected.specialization || 'Not set'}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Cases</p>
                  <p className="font-mono font-medium mt-0.5">{selected.case_count}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Plan</p>
                  <p className="font-medium capitalize mt-0.5">{selected.subscription?.plan?.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                {selected.role === 'doctor' && !selected.is_verified && !selected.is_blocked && (
                  <Button onClick={() => handleAction(selected._id, 'verify')} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2 flex-1">
                    <CheckCircle className="w-4 h-4" /> Verify
                  </Button>
                )}
                {selected.role === 'doctor' && !selected.is_blocked && (
                  <Button onClick={() => handleAction(selected._id, 'block')} variant="outline" className="border-[#8B2626] text-[#8B2626] gap-2 flex-1">
                    <XCircle className="w-4 h-4" /> Block
                  </Button>
                )}
                {selected.role === 'doctor' && selected.is_blocked && (
                  <Button onClick={() => handleAction(selected._id, 'unblock')} variant="outline" className="border-amber-600 text-amber-600 gap-2 flex-1">
                    <RotateCcw className="w-4 h-4" /> Unblock
                  </Button>
                )}
                {isSuperAdmin && selected.role === 'doctor' && (
                  <Button onClick={() => handleAction(selected._id, 'promote')} variant="outline" className="border-purple-600 text-purple-600 gap-2 flex-1">
                    <Crown className="w-4 h-4" /> Promote to Admin
                  </Button>
                )}
                {isSuperAdmin && selected.role === 'admin' && (
                  <Button onClick={() => handleAction(selected._id, 'demote')} variant="outline" className="border-stone-400 text-stone-600 gap-2 flex-1">
                    Demote to Doctor
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grant Complimentary Subscription Modal */}
      <Dialog open={!!grantTarget} onOpenChange={(o) => { if (!o) { setGrantTarget(null); setGrantNote(''); } }}>
        <DialogContent className="max-w-md" data-testid="grant-subscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-emerald-600" /> Grant Complimentary Subscription</DialogTitle>
            <DialogDescription>
              {grantTarget ? `For ${grantTarget.name || grantTarget.email}` : ''} — assigns the plan at zero cost. Use this for VIPs, influencers, or coupon-blocked existing users.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Plan</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { id: '3_months', label: 'Starter (3 mo)', price: '₹2,999' },
                  { id: '6_months', label: 'Pro (6 mo)', price: '₹5,500' },
                  { id: '1_year', label: 'Clinic (1 yr)', price: '₹9,999' },
                  { id: 'pay_per_case', label: 'Pay Per Case', price: '₹99' },
                ].map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setGrantPlan(p.id)}
                    className={`py-2.5 px-3 rounded-md border-2 text-sm transition-colors text-left ${grantPlan === p.id ? 'border-emerald-600 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'}`}
                    data-testid={`grant-plan-${p.id}`}
                  >
                    <p className="font-medium text-stone-900">{p.label}</p>
                    <p className="text-[10px] text-stone-500 mt-0.5 font-mono">retail {p.price} · FREE here</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="grant-note" className="text-sm font-medium">Note <span className="text-stone-400 font-normal">(optional, internal)</span></Label>
              <Input id="grant-note" value={grantNote} onChange={(e) => setGrantNote(e.target.value)} placeholder="e.g. Influencer Dr Sharma, KAMALPOLYCLINIC promo, etc." maxLength={200} className="mt-1" data-testid="grant-note-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setGrantTarget(null)}>Cancel</Button>
              <Button onClick={submitGrant} disabled={granting} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="submit-grant-btn">
                {granting ? '...' : <Gift className="w-4 h-4" />} Grant Plan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
